import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, Plus, X, Loader2, AlertTriangle, Landmark, Download, Percent, Clock, RefreshCw, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { RegOrdersList } from './RegOrdersPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/financials`;
const ROOT = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TABS = [
  { key: 'orders', label: 'Registration orders' },
  { key: 'deposits', label: 'Deposits' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'overdue', label: 'Overdue' },
];

export const FinancialsPanel = ({ orgId, isDark = true, canConfigureProcessor = false }) => {
  const [tab, setTab] = useState('orders');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [banner, setBanner] = useState({ overdue_count: 0, overdue_total: 0 });
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [addAcct, setAddAcct] = useState(false);
  const [addTx, setAddTx] = useState(false);
  const [txStatus, setTxStatus] = useState('');
  const [exportOpen, setExportOpen] = useState(false);

  const text = isDark ? 'text-white' : 'text-slate-900';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';

  const loadTab = useCallback(async () => {
    if (!orgId) return;
    if (tab === 'orders') { setLoading(false); return; }
    setLoading(true);
    try { const r = await axios.get(`${API}/${orgId}/${tab}${tab === 'transactions' && txStatus ? `?status=${txStatus}` : ''}`); setData(r.data); }
    catch { setData({}); } finally { setLoading(false); }
  }, [orgId, tab, txStatus]);
  const loadSide = useCallback(async () => {
    if (!orgId) return;
    try {
      const [a, s] = await Promise.all([axios.get(`${API}/${orgId}/accounts`), axios.get(`${API}/${orgId}/summary`)]);
      setAccounts(a.data.accounts || []); setBanner(s.data);
    } catch { /* ignore */ }
  }, [orgId]);
  useEffect(() => { loadTab(); }, [loadTab]);
  useEffect(() => { loadSide(); }, [loadSide]);

  // Payment Records export — the single export surfaced here (the AD's other
  // exports are covered by OL's other export engines). CSV download.
  const exportPayments = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/exports/payments/${orgId}?format=csv`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="financials-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className={`text-lg font-bold ${text} flex items-center gap-2`}><DollarSign className="h-5 w-5 text-amber-500" /> Financials</h2>
          <p className={`text-sm ${sub}`}>Deposits, transactions, scheduled and overdue payments.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={tab === 'orders'} onClick={() => setExportOpen(true)} data-testid="fin-export-tab"><Download className="h-3.5 w-3.5 mr-1" /> Export</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportPayments} data-testid="fin-export-payments"><Download className="h-3.5 w-3.5 mr-1" /> Payment records</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddTx(true)} data-testid="add-transaction"><Plus className="h-3.5 w-3.5 mr-1" /> Record payment</Button>
        </div>
      </div>

      <RegFinancials orgId={orgId} isDark={isDark} />

      <RegFinancialsConfig orgId={orgId} isDark={isDark} />


      {banner.overdue_count > 0 && !bannerDismissed && (
        <div className={`flex items-center gap-3 border rounded px-3 py-2 text-sm ${isDark ? 'bg-red-950/60 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="overdue-banner">
          <AlertTriangle className="h-4 w-4" /> {banner.overdue_count} payment{banner.overdue_count !== 1 ? 's are' : ' is'} overdue ({money(banner.overdue_total)})
          <button onClick={() => setTab('overdue')} className="underline ml-1">Review now</button>
          <button onClick={() => setBannerDismissed(true)} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="flex gap-6">
        <div className="flex-1 space-y-3">
          <div className="flex gap-1 border-b border-slate-700">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`fin-tab-${t.key}`}
                className={`px-3 py-1.5 text-xs ${tab === t.key ? 'border-b-2 border-amber-500 text-amber-400' : sub}`}>{t.label}</button>
            ))}
          </div>

          {loading ? (
            <div className={`flex items-center gap-2 ${sub} py-10 justify-center`}><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
          ) : (
            <div className={`border rounded-lg overflow-x-auto ${card}`}>
              {tab === 'orders' && <RegOrdersList orgId={orgId} isDark={isDark} />}
              {tab === 'deposits' && <DepositsTable rows={data.deposits} sub={sub} text={text} />}
              {tab === 'transactions' && (
                <div className="flex gap-1 p-2 border-b border-slate-700" data-testid="tx-status-chips">
                  {['', 'Succeeded', 'Pending', 'Failed', 'Refunded'].map(k => (
                    <button key={k || 'all'} onClick={() => setTxStatus(k)} className={`text-[11px] px-2 py-0.5 rounded-full border ${txStatus === k ? 'bg-amber-600 border-amber-500 text-white' : 'border-slate-600 ' + sub}`} data-testid={`tx-status-${k || 'all'}`}>
                      {k || 'All'}{k && data.by_status ? ` (${data.by_status[k] ?? 0})` : ''}</button>
                  ))}
                </div>
              )}
              {tab === 'transactions' && <TransactionsTable rows={data.transactions} totals={data.totals} sub={sub} text={text} />}
              {tab === 'scheduled' && <ScheduledTable rows={data.scheduled} total={data.total} sub={sub} text={text} />}
              {tab === 'overdue' && <OverdueTable rows={data.overdue} total={data.total} sub={sub} text={text} />}
            </div>
          )}
        </div>

        {/* Right rail — deposit accounts */}
        <div className="w-56 shrink-0 space-y-2">
          <div className="flex items-center justify-between"><h4 className={`text-xs font-semibold ${text}`}>Deposit accounts</h4>
            {canConfigureProcessor && <button onClick={() => setAddAcct(true)} className="text-amber-400 hover:text-amber-300" data-testid="add-account" title="Payment-processor config (Tech Specialist)"><Plus className="h-4 w-4" /></button>}</div>
          {!canConfigureProcessor && <p className={`text-[10px] ${sub} italic`}>Processor config is managed by the Tech Specialist.</p>}
          {accounts.length === 0 && <p className={`text-[11px] ${sub}`}>No accounts yet.</p>}
          {accounts.map(a => (
            <div key={a.id} className={`border rounded p-2 flex items-center gap-2 ${card}`} data-testid={`account-${a.id}`}>
              {canConfigureProcessor && <button title="Delete (only when no registration uses it)" className="order-last text-slate-400 hover:text-red-400" data-testid={`account-delete-${a.id}`}
                onClick={async () => { if (!window.confirm('Delete this deposit account?')) return; try { await axios.delete(`${API}/${orgId}/accounts/${a.id}`); toast.success('Deleted'); loadSide(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); } }}><X className="h-3.5 w-3.5" /></button>}
              <Landmark className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0"><div className={`text-xs ${text} truncate`}>{a.bank} *{a.mask}</div>
                <div className="text-[10px] text-emerald-400">{a.status}</div></div>
            </div>
          ))}
        </div>
      </div>

      {exportOpen && <ExportDialog orgId={orgId} tab={tab} status={txStatus} onClose={() => setExportOpen(false)} />}
      {addAcct && canConfigureProcessor && <AddAccount orgId={orgId} onClose={() => setAddAcct(false)} onDone={() => { setAddAcct(false); loadSide(); }} />}
      {addTx && <AddTransaction orgId={orgId} accounts={accounts} onClose={() => setAddTx(false)} onDone={() => { setAddTx(false); loadTab(); loadSide(); }} />}
    </div>
  );
};

const Empty = ({ sub, msg }) => <tr><td colSpan={8} className={`px-3 py-8 text-center ${sub}`}>{msg}</td></tr>;

const PILL = { Succeeded: 'bg-emerald-900 text-emerald-300', Pending: 'bg-amber-900 text-amber-300', Failed: 'bg-red-900 text-red-300', Refunded: 'bg-violet-900 text-violet-300' };

const DepositsTable = ({ rows = [], sub, text }) => {
  const [open, setOpen] = useState(null);
  return (
    <table className="w-full text-xs" data-testid="deposits-table">
      <thead className={sub}><tr className="text-left border-b border-slate-700"><th className="px-3 py-2">Est. deposit date</th><th className="px-3 py-2">Deposit account</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Gross</th><th className="px-3 py-2 text-right">Fees</th><th className="px-3 py-2 text-right">Net</th></tr></thead>
      <tbody>{rows.length === 0 ? <Empty sub={sub} msg="No deposits at this time" /> : rows.map((r, i) => (
        <React.Fragment key={r.id || i}>
          <tr className={`border-b border-slate-800 ${text} cursor-pointer hover:bg-slate-800/40`} onClick={() => setOpen(open === i ? null : i)} data-testid={`deposit-row-${i}`}>
            <td className="px-3 py-2">{r.est_deposit_date || '—'} <span className={sub}>{open === i ? '−' : '+'}</span></td><td className="px-3 py-2">{r.deposit_account}</td>
            <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] ${PILL[r.status] || ''}`}>{r.status}</span></td>
            <td className="px-3 py-2 text-right">{money(r.gross)}</td><td className="px-3 py-2 text-right text-slate-400">{money(r.fees)}</td><td className="px-3 py-2 text-right">{money(r.net)}</td></tr>
          {open === i && (r.payments || []).map((pmt, j) => (
            <tr key={j} className={`border-b border-slate-800/60 ${sub}`} data-testid="deposit-payment">
              <td className="px-6 py-1">{(pmt.payment_date || '').slice(0, 10)} · {pmt.confirmation}</td><td className="px-3 py-1">{pmt.participants_label} · {pmt.source_form}</td><td className="px-3 py-1">{pmt.payment_type}</td>
              <td className="px-3 py-1 text-right">{money(pmt.gross)}</td><td className="px-3 py-1 text-right">{money(pmt.online_fee)}</td><td className="px-3 py-1 text-right">{money(pmt.net)}</td></tr>
          ))}
        </React.Fragment>
      ))}</tbody>
    </table>
  );
};

const TransactionsTable = ({ rows = [], totals = {}, sub, text }) => (
  <>
    <table className="w-full text-xs">
      <thead className={sub}><tr className="text-left border-b border-slate-700"><th className="px-3 py-2">Payment date</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Confirmation</th><th className="px-3 py-2">Participant(s)</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Gross</th><th className="px-3 py-2 text-right">Fee</th><th className="px-3 py-2 text-right">Net</th></tr></thead>
      <tbody>{rows.length === 0 ? <Empty sub={sub} msg="No transactions at this time" /> : rows.map((r, i) => (
        <tr key={i} className={`border-b border-slate-800 ${text}`}><td className="px-3 py-2">{(r.payment_date || '').slice(0, 10)}</td>
          <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] ${PILL[r.status] || ''}`}>{r.status}</span></td>
          <td className="px-3 py-2">{r.confirmation}</td>
          <td className="px-3 py-2">{r.participants_label}</td><td className="px-3 py-2">{r.source_form}{r.source_group ? ` · ${r.source_group}` : ''}</td><td className="px-3 py-2">{r.payment_type}</td>
          <td className="px-3 py-2 text-right">{money(r.gross)}</td><td className="px-3 py-2 text-right text-slate-400">{money(r.online_fee)}</td><td className="px-3 py-2 text-right">{money(r.net)}</td></tr>
      ))}</tbody>
    </table>
    {rows.length > 0 && <div className={`px-3 py-2 text-xs ${sub} border-t border-slate-700 flex gap-4 justify-end`}>Gross {money(totals.gross)} · Fees {money(totals.online_fees)} · <span className="text-white">Net {money(totals.net)}</span></div>}
  </>
);

const ScheduledTable = ({ rows = [], total = 0, sub, text }) => (
  <>
    <table className="w-full text-xs">
      <thead className={sub}><tr className="text-left border-b border-slate-700"><th className="px-3 py-2">Due date</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Confirmation</th><th className="px-3 py-2">Participant(s)</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Est. Net</th></tr></thead>
      <tbody>{rows.length === 0 ? <Empty sub={sub} msg="No financial records at this time" /> : rows.map((r, i) => (
        <tr key={i} className={`border-b border-slate-800 ${text}`}><td className="px-3 py-2">{(r.due_date || '').slice(0, 10)}</td>
          <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-[10px] bg-slate-700 text-slate-300">{r.status}</span></td>
          <td className="px-3 py-2">{r.confirmation}</td><td className="px-3 py-2">{r.participant}</td><td className="px-3 py-2">{r.source_form}</td><td className="px-3 py-2">{r.payment_type}</td><td className="px-3 py-2 text-right">{money(r.est_net)}</td></tr>
      ))}</tbody>
    </table>
    <div className={`px-3 py-2 text-xs ${sub} border-t border-slate-700 text-right`}>Total: <span className="text-white">{money(total)}</span></div>
  </>
);

const OverdueTable = ({ rows = [], total = 0, sub, text }) => (
  <>
    <table className="w-full text-xs">
      <thead className={sub}><tr className="text-left border-b border-slate-700"><th className="px-3 py-2">Due date</th><th className="px-3 py-2">Confirmation</th><th className="px-3 py-2">Participant(s)</th><th className="px-3 py-2 text-right">Overdue amount</th><th className="px-3 py-2">Days overdue</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Program</th><th className="px-3 py-2">Type</th></tr></thead>
      <tbody>{rows.length === 0 ? <Empty sub={sub} msg="No overdue payments 🎉" /> : rows.map((r, i) => (
        <tr key={i} className={`border-b border-slate-800 ${text}`}><td className="px-3 py-2">{r.due_date || '—'}{r.installments_overdue > 1 ? <span className={sub}> ({r.installments_overdue} installments)</span> : ''}</td><td className="px-3 py-2">{r.confirmation}</td><td className="px-3 py-2">{r.participant}</td>
          <td className="px-3 py-2 text-right text-red-400">{money(r.overdue_amount)}</td>
          <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] ${r.days_overdue === null ? 'bg-amber-900 text-amber-300' : 'bg-red-900 text-red-300'}`}>{r.days_overdue === null ? 'Unscheduled' : `${r.days_overdue} days`}</span></td>
          <td className="px-3 py-2">{r.source_form}</td><td className="px-3 py-2">{r.program}</td><td className="px-3 py-2">{r.payment_type}</td></tr>
      ))}</tbody>
    </table>
    <div className={`px-3 py-2 text-xs ${sub} border-t border-slate-700 text-right`}>Total: <span className="text-red-400">{money(total)}</span></div>
  </>
);

const AddAccount = ({ orgId, onClose, onDone }) => {
  const [bank, setBank] = useState(''); const [mask, setMask] = useState(''); const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await axios.post(`${API}/${orgId}/accounts`, { bank, mask }); toast.success('Account added'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose} data-testid="add-account-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 w-80 space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-white">Add deposit account</h3>
        <Input className="h-8 text-sm bg-slate-800 border-slate-600 text-white" placeholder="Bank name (e.g. FCB)" value={bank} onChange={e => setBank(e.target.value)} data-testid="acct-bank" />
        <Input className="h-8 text-sm bg-slate-800 border-slate-600 text-white" placeholder="Last 4 digits" value={mask} onChange={e => setMask(e.target.value)} data-testid="acct-mask" />
        <div className="flex gap-2 justify-end"><Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={save} data-testid="acct-save">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}</Button></div>
      </div>
    </div>
  );
};

const AddTransaction = ({ orgId, accounts, onClose, onDone }) => {
  const [f, setF] = useState({ participant: '', gross: '', online_fee: '', source_form: '', deposit_account_id: accounts[0]?.id || '', status: 'Succeeded', due_date: '', scheduled: false });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF(v => ({ ...v, ...p }));
  const save = async () => {
    if (!f.participant.trim() || !f.gross) { toast.error('Participant and gross amount required'); return; }
    setBusy(true);
    const body = { participant: f.participant, gross: parseFloat(f.gross), online_fee: parseFloat(f.online_fee || 0), source_form: f.source_form, deposit_account_id: f.deposit_account_id || null, status: f.status };
    if (f.scheduled && f.due_date) { body.status = 'Pending'; body.schedule = [{ due_date: f.due_date, amount: parseFloat(f.gross), paid: false }]; }
    try { await axios.post(`${API}/${orgId}/transactions`, body); toast.success('Payment recorded'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose} data-testid="add-transaction-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 w-96 space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-white">Record payment</h3>
        <Input className="h-8 text-sm bg-slate-800 border-slate-600 text-white" placeholder="Participant(s)" value={f.participant} onChange={e => set({ participant: e.target.value })} data-testid="tx-participant" />
        <div className="flex gap-2">
          <Input type="number" className="h-8 text-sm bg-slate-800 border-slate-600 text-white" placeholder="Gross $" value={f.gross} onChange={e => set({ gross: e.target.value })} data-testid="tx-gross" />
          <Input type="number" className="h-8 text-sm bg-slate-800 border-slate-600 text-white" placeholder="Online fee $" value={f.online_fee} onChange={e => set({ online_fee: e.target.value })} data-testid="tx-fee" />
        </div>
        <Input className="h-8 text-sm bg-slate-800 border-slate-600 text-white" placeholder="Source (registration/program)" value={f.source_form} onChange={e => set({ source_form: e.target.value })} data-testid="tx-source" />
        <select className="h-8 text-sm bg-slate-800 border border-slate-600 text-white rounded w-full px-2" value={f.deposit_account_id} onChange={e => set({ deposit_account_id: e.target.value })} data-testid="tx-account">
          <option value="">No deposit account</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.bank} *{a.mask}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-white"><input type="checkbox" checked={f.scheduled} onChange={e => set({ scheduled: e.target.checked })} data-testid="tx-scheduled" /> Schedule this as an installment (due date)</label>
        {f.scheduled && <Input type="date" className="h-8 text-sm bg-slate-800 border-slate-600 text-white" value={f.due_date} onChange={e => set({ due_date: e.target.value })} data-testid="tx-due" />}
        <div className="flex gap-2 justify-end"><Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={save} data-testid="tx-save">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}</Button></div>
      </div>
    </div>
  );
};

export default FinancialsPanel;

// Registration-DERIVED financials (single source = reg_submissions): fees / paid
// / outstanding per registration + submission. Real payments/invoices remain in
// the tabs above/right rail when they exist.
function RegFinancials({ orgId, isDark }) {
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState({ fees: 0, paid: 0, outstanding: 0, registrations: 0 });
  const [open, setOpen] = useState({});
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-slate-200' : 'text-slate-800';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const m = (n) => `$${Number(n || 0).toFixed(2)}`;
  useEffect(() => {
    axios.get(`${API}/${orgId}/registrations`)
      .then((r) => { setGroups(r.data.groups || []); setTotals(r.data.totals || {}); })
      .catch(() => { setGroups([]); });
  }, [orgId]);
  return (
    <div className={`border rounded-lg p-3 ${card}`} data-testid="fin-registrations">
      <div className="flex items-center gap-3 flex-wrap mb-2">
        <h4 className={`text-sm font-semibold ${text}`}>Registration fees (live from registrations)</h4>
        <span className={`text-[11px] ${sub}`}>{totals.registrations} registrations · fees {m(totals.fees)} · paid {m(totals.paid)} · outstanding {m(totals.outstanding)}</span>
      </div>
      {groups.length === 0 ? <p className={`text-[11px] ${sub}`}>No registrations yet.</p> : (
        <div className="space-y-1">
          {groups.map((g) => (
            <div key={g.form_id} className="border-b border-slate-100 dark:border-slate-700/50 pb-1">
              <button className={`w-full flex items-center gap-2 text-xs py-1 ${text}`} onClick={() => setOpen((o) => ({ ...o, [g.form_id]: !o[g.form_id] }))} data-testid={`fin-reg-form-${g.form_id}`}>
                <span className="font-medium">{g.form_name}</span>
                <span className={sub}>{g.rows.length} reg · fees {m(g.fees)} · paid {m(g.paid)} · outstanding {m(g.outstanding)}</span>
                <span className="ml-auto">{open[g.form_id] ? '−' : '+'}</span>
              </button>
              {open[g.form_id] && (
                <table className="w-full text-[11px] mt-1">
                  <thead><tr className={`text-left ${sub}`}><th>Player</th><th>Status</th><th>Fee</th><th>Paid</th><th>Outstanding</th></tr></thead>
                  <tbody>{g.rows.map((r) => (
                    <tr key={r.submission_id} className={text}><td>{r.player}</td><td>{r.payment_status || '—'}</td><td>{m(r.fee)}</td><td>{m(r.paid)}</td><td>{m(r.outstanding)}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Registration finance CONFIG — discounts, installment schedule, refunds.
// Repointed from the removed AD Settings ▸ Financial/Refunds sections
// (migration_log_0147). Persist on org.settings + /api/payments; no new routes.
function RegFinancialsConfig({ orgId, isDark }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discount, setDiscount] = useState({ multi_child_discount_pct: 0, coach_parent_discount_pct: 0, scholarship_discount_pct: 0, early_bird_discount_pct: 0, early_bird_deadline: '' });
  const [schedule, setSchedule] = useState({ enabled: false, installments: 2, interval_days: 30 });
  const [refunds, setRefunds] = useState([]);

  const loadRefunds = useCallback(async () => {
    if (!orgId) return;
    try { const { data } = await axios.get(`${ROOT}/api/payments/refunds/${orgId}`); setRefunds(data || []); } catch { setRefunds([]); }
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${ROOT}/api/organizations/${orgId}`).then(({ data }) => {
      const s = data.settings || {};
      setDiscount({
        multi_child_discount_pct: s.multi_child_discount_pct || 0,
        coach_parent_discount_pct: s.coach_parent_discount_pct || 0,
        scholarship_discount_pct: s.scholarship_discount_pct || 0,
        early_bird_discount_pct: s.early_bird_discount_pct || 0,
        early_bird_deadline: s.early_bird_deadline || '',
      });
      setSchedule(s.payment_schedule || { enabled: false, installments: 2, interval_days: 30 });
      setRemindDays(s.payment_reminder_days ?? '');
    }).catch(() => {});
    loadRefunds();
  }, [orgId, loadRefunds]);

  const saveDiscounts = async () => {
    setSaving(true);
    try { await axios.put(`${ROOT}/api/organizations/${orgId}`, { settings: discount }); toast.success('Discount rules saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const [remindDays, setRemindDays] = useState('');
  const saveRemind = async () => {
    setSaving(true);
    try { await axios.put(`${ROOT}/api/organizations/${orgId}`, { settings: { payment_reminder_days: remindDays === '' ? null : parseInt(remindDays, 10) } }); toast.success('Reminder setting saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const saveSchedule = async () => {
    setSaving(true);
    try { await axios.put(`${ROOT}/api/organizations/${orgId}`, { settings: { payment_schedule: schedule } }); toast.success('Payment schedule saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const refundAction = async (id, action) => {
    try { await axios.put(`${ROOT}/api/payments/refund/${id}`, { action }); toast.success(`Refund ${action}d`); loadRefunds(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const inp = isDark ? 'bg-slate-700 border-slate-600 text-white' : '';

  return (
    <div className={`border rounded-lg ${card}`} data-testid="fin-config">
      <button className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold ${text}`} onClick={() => setOpen(o => !o)} data-testid="fin-config-toggle">
        <Percent className="h-4 w-4 text-green-500" /> Registration finance settings
        <span className="ml-auto">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
      </button>
      {open && (
        <div className="p-3 pt-0 space-y-5">
          {/* Discounts */}
          <div>
            <p className={`text-sm font-medium mb-2 ${text}`}>Discount Rules</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={`text-xs ${sub}`}>Multi-Child (%)</Label><Input type="number" min="0" max="100" value={discount.multi_child_discount_pct} onChange={e => setDiscount({ ...discount, multi_child_discount_pct: parseFloat(e.target.value) || 0 })} className={inp} data-testid="discount-multi-child" /></div>
              <div><Label className={`text-xs ${sub}`}>Coach Parent (%)</Label><Input type="number" min="0" max="100" value={discount.coach_parent_discount_pct} onChange={e => setDiscount({ ...discount, coach_parent_discount_pct: parseFloat(e.target.value) || 0 })} className={inp} data-testid="discount-coach" /></div>
              <div><Label className={`text-xs ${sub}`}>Scholarship (%)</Label><Input type="number" min="0" max="100" value={discount.scholarship_discount_pct} onChange={e => setDiscount({ ...discount, scholarship_discount_pct: parseFloat(e.target.value) || 0 })} className={inp} data-testid="discount-scholarship" /></div>
              <div><Label className={`text-xs ${sub}`}>Early Bird (%)</Label><Input type="number" min="0" max="100" value={discount.early_bird_discount_pct} onChange={e => setDiscount({ ...discount, early_bird_discount_pct: parseFloat(e.target.value) || 0 })} className={inp} data-testid="discount-early-bird" /></div>
            </div>
            {discount.early_bird_discount_pct > 0 && (
              <div className="mt-2"><Label className={`text-xs ${sub}`}>Early Bird Deadline</Label><Input type="date" value={discount.early_bird_deadline} onChange={e => setDiscount({ ...discount, early_bird_deadline: e.target.value })} className={inp} data-testid="discount-deadline" /></div>
            )}
            <Button size="sm" className="mt-2 h-8 bg-green-600 hover:bg-green-700" onClick={saveDiscounts} disabled={saving} data-testid="save-discounts-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Save Discounts</>}</Button>
          </div>

          {/* Payment schedule */}
          <div>
            <p className={`text-sm font-medium mb-2 flex items-center gap-1 ${text}`}><Clock className="h-4 w-4 text-blue-500" />Payment Schedule</p>
            <label className={`flex items-center gap-2 text-sm mb-2 ${text}`}>
              <input type="checkbox" checked={schedule.enabled} onChange={e => setSchedule({ ...schedule, enabled: e.target.checked })} data-testid="sched-enabled" /> Enable installment payments
            </label>
            {schedule.enabled && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className={`text-xs ${sub}`}>Number of Installments</Label><Input type="number" min="2" max="12" value={schedule.installments} onChange={e => setSchedule({ ...schedule, installments: parseInt(e.target.value) || 2 })} className={inp} data-testid="sched-installments" /></div>
                <div><Label className={`text-xs ${sub}`}>Days Between Installments</Label><Input type="number" min="7" max="90" value={schedule.interval_days} onChange={e => setSchedule({ ...schedule, interval_days: parseInt(e.target.value) || 30 })} className={inp} data-testid="sched-interval" /></div>
              </div>
            )}
            <Button size="sm" className="mt-2 h-8 bg-blue-600 hover:bg-blue-700" onClick={saveSchedule} disabled={saving} data-testid="save-schedule-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Save Schedule</>}</Button>
          </div>

          {/* Due-date reminders (installments + invoices) */}
          <div data-testid="fin-remind">
            <p className={`text-sm font-medium mb-2 flex items-center gap-1 ${text}`}><Clock className="h-4 w-4 text-amber-500" />Payment reminders</p>
            <div className="flex items-end gap-2">
              <div className="w-40"><Label className={`text-xs ${sub}`}>Days before due date</Label>
                <Input type="number" min="0" max="60" placeholder="off" value={remindDays} onChange={e => setRemindDays(e.target.value)} className={inp} data-testid="remind-days" /></div>
              <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700" onClick={saveRemind} disabled={saving} data-testid="save-remind-btn"><Save className="h-3 w-3 mr-1" />Save</Button>
            </div>
            <p className={`text-[11px] mt-1 ${sub}`}>Families get one email per installment or invoice this many days before it is due (every primary contact). Leave blank to turn off.</p>
          </div>

          {/* Refunds */}
          <div>
            <p className={`text-sm font-medium mb-2 flex items-center gap-1 ${text}`}><RefreshCw className="h-4 w-4 text-orange-500" />Refund Requests</p>
            {refunds.length === 0 ? (
              <p className={`text-[11px] ${sub}`}>No refund requests pending.</p>
            ) : (
              <div className="space-y-2">
                {refunds.map(r => (
                  <div key={r.id} className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid={`refund-${r.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <span className={`font-medium text-sm ${text}`}>{r.family_name}</span>
                        <div className={`text-xs ${sub}`}>${r.refund_amount?.toFixed(2) || '0.00'} of ${r.original_amount?.toFixed(2) || '0.00'}{r.reason ? ` · ${r.reason}` : ''}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] ${r.status === 'pending' ? 'bg-amber-900 text-amber-300' : r.status === 'approved' ? 'bg-blue-900 text-blue-300' : r.status === 'processed' ? 'bg-emerald-900 text-emerald-300' : 'bg-red-900 text-red-300'}`}>{r.status}</span>
                        {r.status === 'pending' && (<>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 px-2 text-xs" onClick={() => refundAction(r.id, 'approve')} data-testid={`approve-refund-${r.id}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 border-red-500/30" onClick={() => refundAction(r.id, 'reject')} data-testid={`reject-refund-${r.id}`}>Reject</Button>
                        </>)}
                        {r.status === 'approved' && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-7 px-2 text-xs" onClick={() => refundAction(r.id, 'process')} data-testid={`process-refund-${r.id}`}>Process</Button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// F12 — export the current Financials tab as CSV with chosen columns; layouts can be
// saved as named templates (shared by the org's admins).
function ExportDialog({ orgId, tab, status, onClose }) {
  const [all, setAll] = useState([]);
  const [cols, setCols] = useState([]);
  const [tpls, setTpls] = useState([]);
  const [name, setName] = useState('');
  const load = useCallback(async () => {
    const [c, t] = await Promise.all([axios.get(`${API}/${orgId}/export-columns`), axios.get(`${API}/${orgId}/export-templates`)]);
    const list = (c.data.columns || {})[tab] || []; setAll(list); setCols(list);
    setTpls((t.data.templates || []).filter(x => x.tab === tab));
  }, [orgId, tab]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const download = () => {
    const q = new URLSearchParams({ columns: cols.join(',') }); if (tab === 'transactions' && status) q.set('status', status);
    window.open(`${API}/${orgId}/export/${tab}?${q.toString()}`, '_blank');
  };
  const saveTpl = async () => {
    if (!name.trim()) return;
    try { await axios.post(`${API}/${orgId}/export-templates`, { tab, name, columns: cols }); setName(''); load(); toast.success('Template saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const delTpl = async (t) => { await axios.delete(`${API}/${orgId}/export-templates/${t.id}`); load(); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="export-dialog">
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 w-full max-w-md space-y-3 text-white" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between"><h3 className="font-bold capitalize">Export {tab}</h3><button onClick={onClose}><X className="h-4 w-4" /></button></div>
        {tpls.length > 0 && (
          <div className="flex flex-wrap gap-1">{tpls.map(t => (
            <span key={t.id} className="text-[11px] border border-slate-600 rounded-full px-2 py-0.5 flex items-center gap-1">
              <button onClick={() => setCols(t.columns)} data-testid={`export-tpl-${t.id}`}>{t.name}</button>
              <button onClick={() => delTpl(t)} className="text-slate-400 hover:text-red-400"><X className="h-3 w-3" /></button></span>))}</div>
        )}
        <div className="grid grid-cols-2 gap-1 text-xs">
          {all.map(c => (
            <label key={c} className="flex items-center gap-2"><input type="checkbox" checked={cols.includes(c)} onChange={e => setCols(x => e.target.checked ? all.filter(a => x.includes(a) || a === c) : x.filter(y => y !== c))} />{c.replace(/_/g, ' ')}</label>
          ))}
        </div>
        <div className="flex gap-2"><Input className="h-8 text-sm bg-slate-800 border-slate-600" placeholder="Save these columns as…" value={name} onChange={e => setName(e.target.value)} data-testid="export-tpl-name" />
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={saveTpl} data-testid="export-tpl-save">Save</Button></div>
        <Button size="sm" className="w-full h-8 bg-amber-600 hover:bg-amber-700" disabled={!cols.length} onClick={download} data-testid="export-download"><Download className="h-3.5 w-3.5 mr-1" /> Download CSV</Button>
      </div>
    </div>
  );
}

